/**
 * How a stock adjustment's reason is worded (FR-INV-7).
 *
 * `AdjustmentReason` is an open union on purpose: the five the form offers, and
 * whatever someone types under "Other…". One function words all of them, so the
 * Adjustments list, its export and anything else that shows a reason say
 * "RTO received" and never "Rto received", which is what a generic
 * underscore-to-space-and-capitalise gives for a word that is an acronym.
 */

/** The reasons the ERP knows, and what each is called to a person. */
export const ADJUSTMENT_REASON_LABELS: Readonly<Record<string, string>> = {
  rto_received: "RTO received",
  damaged: "Damaged",
  lost: "Lost",
  stock_take: "Stock take",
  correction: "Correction",
  // The Update stock screen (#602).
  received_from_vendor: "Received from vendor",
  // No longer offered (#618 split it into Damaged and Lost); old rows keep it.
  damaged_or_lost: "Damaged or lost",
  returned_to_shelf: "Returned to shelf",
};

/** Words that stay in capitals when a code is turned into words. */
const ACRONYMS: ReadonlySet<string> = new Set([
  "rto",
  "cod",
  "gst",
  "hsn",
  "sku",
  "upi",
]);

/**
 * The words for a reason. A known reason has its label; anything typed by a
 * person is left as written; a code-shaped value (`some_new_reason`, no spaces)
 * is turned into words, keeping acronyms in capitals.
 */
export function adjustmentReasonLabel(reason: string): string {
  const known = ADJUSTMENT_REASON_LABELS[reason];
  if (known !== undefined) return known;

  const text = reason.trim();
  if (text === "" || text.includes(" ") || !text.includes("_")) return text;

  const words = text.split("_").filter((word) => word !== "");
  const spelled = words.map((word) =>
    ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word,
  );
  const first = spelled[0] ?? "";
  spelled[0] = ACRONYMS.has(first.toLowerCase())
    ? first.toUpperCase()
    : first.charAt(0).toUpperCase() + first.slice(1);
  return spelled.join(" ");
}
